import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map as rxMap, shareReplay, take } from 'rxjs/operators';
import { TDistrictId } from 'Src/ng2/shared/typings/interfaces/district.interface';
import { TOpEntityType } from 'Src/ng2/shared/typings/types/entity-type.types';
import { TEntityMode } from 'Src/ng2/shared/typings/types/entity-mode.types';
import { ApiService } from 'Src/ng2/shared/services/api-service/api-service';
import {
  IFeatureSetNavConfigResult,
  IFeatureSetNavConfigResultDecorated,
  IFeatureSetNavItem,
  IFeatureSetNavItemDecorated,
} from 'Src/ng2/shared/typings/interfaces/feature-set-nav-config.interface';
import { UrlPathService } from 'Src/ng2/shared/services/url-path-service/url-path.service';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { Toggles } from 'Src/ng2/shared/constants/toggles.constant';

export interface INavItemsContext {
  entityMode: TEntityMode;
  entityId?: string | null; // if null, it means user is on multi entity view, otherwise user is on single entity view
  entityType: TOpEntityType | null;
  networkId?: string | null;
  districtId?: TDistrictId | null; // if null, it means user is on non-district specific view, otherwise user is on district specific view
  subEntityId?: string | null;
  subEntityParamName?: string | null;
  parent?: string | null;
  expandAs?: 'accordion' | null;
}

interface IActivePathMatch {
  item: IFeatureSetNavItem;
  score: number;
}

@Injectable({ providedIn: 'root' })
export class NavItemsService {
  private navItems$: Observable<IFeatureSetNavConfigResultDecorated>;
  private configContextKey: string;

  constructor (
    private apiService: ApiService,
    private urlPathService: UrlPathService,
    private toggleService: ToggleService,
  ) {}

  public getNavItems$ ({ context }: { context: INavItemsContext }): Observable<IFeatureSetNavConfigResultDecorated> {
    const contextKey = this.getContextKey(context);
    if (this.navItems$ && this.configContextKey === contextKey) {
      return this.navItems$;
    }

    this.configContextKey = contextKey;
    this.navItems$ = this.apiService
      .getFeatureSetNavConfig({
        entityType: context.entityType,
        entityMode: context.entityMode,
        entityId: context.entityId,
        networkId: context.networkId,
      })
      .pipe(
        take(1),
        rxMap((navConfig: IFeatureSetNavConfigResult) => this.decorateNavConfig(navConfig, context)),
        shareReplay(1),
      );
    return this.navItems$;
  }

  private getContextKey (context: INavItemsContext): string {
    let resourceId = context?.entityId || context?.networkId || 'NO_RESOURCE_ID';
    if (context?.subEntityId) resourceId = resourceId.concat(`::${context.subEntityId}`);
    return `${context?.entityMode}::${context?.entityType}::${resourceId}::${context?.districtId || 'NO_DISTRICT_ID'}`;
  }

  private decorateNavConfig (
    navConfig: IFeatureSetNavConfigResult,
    context: INavItemsContext,
  ): IFeatureSetNavConfigResultDecorated {
    const rootUrl = this.urlPathService.getPortalUrl({
      entityId: context.entityId,
      entityType: context.entityType,
      networkId: context.networkId,
    });
    const base = (navConfig?.base || []).filter((item) => !this.isNavItemToggledOff(item));
    const optional = (navConfig?.optional || []).filter((item) => !this.isNavItemToggledOff(item));

    return {
      base: base.map((item: IFeatureSetNavItem) => this.decorateItem(item, { ...context, rootUrl })),
      optional: optional.map((item: IFeatureSetNavItem) => this.decorateItem(item, { ...context, rootUrl })),
    };
  }

  // The Families entry is backend-driven by the `family` feature set, so its own toggle is
  // applied here rather than at the route guard the way page access is.
  private isNavItemToggledOff (item: IFeatureSetNavItem): boolean {
    if (item.key === 'family') return !this.toggleService.getToggleState(Toggles.TOGGLE_FAMICOM);
    return false;
  }

  private decorateItem (item: IFeatureSetNavItem, opts: INavItemsContext & { rootUrl: string }): IFeatureSetNavItemDecorated {
    const icon = item.icon;
    const path = item.path;
    const children = item.children || [];
    const queryParams = item.queryParams || {};
    let subEntityParam = {};
    if (opts.subEntityParamName && opts.subEntityId) {
      subEntityParam = { [opts.subEntityParamName]: opts.subEntityId };
    }
    const isParent = children.length > 0;
    const parent = isParent ? item.key : null;

    return {
      ...item,
      children: children.map((child: IFeatureSetNavItem) => this.decorateItem(child, { ...opts, parent })),
      leftDefaultIcon: icon ? icon + '-grey' : null,
      leftSelectedIcon: icon ? icon + '-blue' : null,
      url: path ? `${opts.rootUrl}/${path}` : null,
      queryParams: {
        ...queryParams,
        ...subEntityParam,
      },
      expandAs: isParent ? 'accordion' : null,
      parent: opts.parent || null,
    };
  }

  public findBestActivePathMatch (featureSetNavItems: IFeatureSetNavItem[], activePath: string): IFeatureSetNavItem | null {
    const match = this.findBestActivePathMatchWithScore(featureSetNavItems, activePath);
    return match?.item || null;
  }

  private findBestActivePathMatchWithScore (
    featureSetNavItems: IFeatureSetNavItem[],
    activePath: string,
  ): IActivePathMatch | null {
    let bestMatch: IActivePathMatch = null;

    for (const navItem of featureSetNavItems) {
      if (navItem.children.length) {
        const childMatch = this.findBestActivePathMatchWithScore(navItem.children, activePath);
        bestMatch = this.getHigherPriorityMatch(bestMatch, childMatch);
      }

      const matchScore = this.urlPathService.getPathMatchScore(navItem.path, activePath);
      if (matchScore >= 0) {
        bestMatch = this.getHigherPriorityMatch(bestMatch, {
          item: navItem,
          score: matchScore,
        });
      }
    }

    return bestMatch;
  }

  private getHigherPriorityMatch (current: IActivePathMatch | null, candidate: IActivePathMatch | null): IActivePathMatch | null {
    if (!candidate) return current;
    if (!current) return candidate;
    return candidate.score > current.score ? candidate : current;
  }
}
